"use client";

import React, { useEffect, useState } from "react";

import { usePathname } from "next/navigation";
import Image from "next/image";
import DecorationLine from "@/components/global/ui/DecorationLine";
import Chip from "@/components/global/ui/Chip";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/shadcn/ui/carousel";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GROQImage } from "@/sanity/lib/definitions";
import { PortableTextBlock } from "sanity";
import {
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
  resolveI18nRichtext,
  II18nRichtext,
} from "@/sanity/globals/I18n";

export default function TradersSlider({
  chip,
  text,
  slides,
  className,
}: {
  chip: {
    icon: GROQImage;
    text: string;
  };
  text: PortableTextBlock[];
  slides?: {
    image: GROQImage;
    text: PortableTextBlock[];
    textI18n?: II18nRichtext;
  }[];
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const pathname = usePathname();

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;

  return (
    <section
      className={cn(
        "relative overflow-clip py-[12rem] lg:py-[18rem]",
        className,
      )}
    >
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />

      <div className="relative pb-[3rem] lg:pb-[5rem]">
        <DecorationLine className="bottom-0" orientation="horizontal" />

        <div className="container">
          <div className="mx-auto w-[min(187.75rem,100%)] flex flex-col lg:flex-row items-center lg:items-end lg:justify-between">
            <div className="flex flex-col items-center lg:items-start">
              <Chip image={chip?.icon} text={chip?.text} />

              {text && (
                <div className="richtext lg:richtext-start prose-p:text-[2rem] prose-p:w-[min(92.5rem,100%)] prose-headings:w-[min(121.75rem,100%)]">
                  <PortableText value={text} components={richTextComponent} />
                </div>
              )}
            </div>

            {slides && (
              <div className="hidden lg:flex items-end justify-end flex-shrink-0">
                <div
                  className={cn(
                    "w-full flex gap-[1rem] px-[1.25rem] py-[1.125rem] relative border-[2px] border-white rounded-full",
                    "after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-full after:pointer-events-none",
                  )}
                >
                  <button
                    onClick={() => api?.scrollPrev()}
                    className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                    disabled={!api || !canPrev}
                    aria-label="Previous slide"
                  >
                    <svg
                      viewBox="0 0 12 20"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]"
                    >
                      <path
                        d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={() => api?.scrollNext()}
                    className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                    disabled={!api || !canNext}
                    aria-label="Next slide"
                  >
                    <svg
                      viewBox="0 0 12 20"
                      xmlns="http://www.w3.org/2000/svg"
                      className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]"
                    >
                      <path
                        d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {slides && (
        <div className="container flex flex-col items-center pt-[3rem] lg:pt-[5rem]">
          <div className="w-[min(187.75rem,100%)] flex flex-col items-center">
            <Carousel
              className="w-full [&_>_div]:!overflow-visible"
              setApi={setApi}
              opts={{
                align: "start",
                skipSnaps: false,
              }}
            >
              <CarouselContent className="w-full gap-x-[2rem]">
                {slides?.map((item, index) => (
                  <CarouselItem
                    key={index}
                    className="p-0 basis-[42.5rem] lg:basis-[56rem] flex flex-col items-start"
                  >
                    {item?.image && (
                      <Image
                        className="!w-full mb-[2rem] lg:mb-[2.5rem] aspect-[448/576] object-cover"
                        src={item.image}
                        quality={95}
                        alt=""
                      />
                    )}

                    {resolveI18nRichtext(
                      item?.text,
                      item?.textI18n,
                      language,
                    ) && (
                      <div className="richtext richtext-start prose-headings:text-[3rem] prose-headings:mb-[.5rem]">
                        <PortableText
                          value={
                            resolveI18nRichtext(
                              item?.text,
                              item?.textI18n,
                              language,
                            )!
                          }
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            <div
              className={cn(
                "w-full sm:w-auto flex lg:hidden items-center justify-between sm:justify-center gap-[2.25rem] mt-[2.5rem] px-[1.25rem] py-[1.125rem] relative border-[2px] border-white rounded-full",
                "after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-full after:pointer-events-none",
              )}
            >
              <button
                onClick={() => api?.scrollPrev()}
                className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                disabled={!api || !canPrev}
                aria-label="Previous slide"
              >
                <svg
                  viewBox="0 0 12 20"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]"
                >
                  <path
                    d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z"
                    fill="currentColor"
                  />
                </svg>
              </button>

              <div className="sm:w-[40rem] h-[.5rem] grow sm:grow-0 flex gap-[1rem] px-[.5rem]">
                {slides?.map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      "grow h-full transition-colors",
                      index === current ? "bg-[#202223]" : "bg-[#202223]/20",
                    )}
                  />
                ))}
              </div>

              <button
                onClick={() => api?.scrollNext()}
                className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                disabled={!api || !canNext}
                aria-label="Next slide"
              >
                <svg
                  viewBox="0 0 12 20"
                  xmlns="http://www.w3.org/2000/svg"
                  className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]"
                >
                  <path
                    d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
